// apps/web/src/pages/admin/calendar/CalendarDayTapDialog.tsx — Phase 5 plan 05-05.
//
// Day-tap dialog wired per CONTEXT.md D-12. Renders a thin modal that
// branches on the day-state:
//
//   - AVAILABLE / FULL → "Tag blockieren" dialog. Submit fires
//     createBlockedPeriod(input, forceFlag: false) as a dry-run; if
//     conflicts > 0 + requiresForce: true the inline B5·B warning panel
//     renders the conflict list + the "Betroffene Gäste wurden
//     informiert" checkbox + the "Blocken bestätigen" re-submit button.
//   - OUTSIDE_WINDOW / BLOCKED → "Tag freigeben?" inline confirm. On
//     confirm fires unblockDay(date) and invalidates the admin cache.
//   - PAST → never mounted (the parent CalendarPage early-returns).
//
// Both return sites wrap their markup in React.createPortal(<…>, document.body)
// so the dialog escapes the ReservationsPage subtree and its stacking context.
// The inner panel replaces `.card` (bg-bg equals the page surface) with
// bg-white + shadow-lg + ring-1 ring-divider so it reads as a true popup
// against the bg-black/40 overlay.
//
// The dialog stays mounted across the tap interaction — no router nav — so
// the calendar grid remains in place while the operator negotiates the
// warning modal.

import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ClientError } from "graphql-request";
import { gqlRequest } from "../../../lib/api";
import { useInvalidateAdminCaches } from "../../../lib/admin-cache";
import {
  CREATE_BLOCKED_PERIOD_MUTATION,
  UNBLOCK_DAY_MUTATION,
} from "./calendar-operations";

export type DayStateKind =
  "PAST" | "OUTSIDE_WINDOW" | "BLOCKED" | "FULL" | "AVAILABLE";

interface ReservationStub {
  id: string;
  date: string;
  time: string | null;
  partySize: number;
}

interface BlockResult {
  blockedPeriod: {
    id: string;
    fromDate: string;
    toDate: string;
    fromTime: string | null;
    toTime: string | null;
    reason: string | null;
  } | null;
  conflicts: ReservationStub[];
  conflictingReservationCount: number;
  requiresForce: boolean;
}

export function CalendarDayTapDialog({
  date,
  dayState,
  onClose,
}: {
  date: string;
  dayState: DayStateKind;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const invalidate = useInvalidateAdminCaches();

  const [reason, setReason] = useState("");
  const [forceAck, setForceAck] = useState(false);

  const blockMutation = useMutation({
    mutationFn: async (forceFlag: boolean) => {
      const res = await gqlRequest<{
        createBlockedPeriod: BlockResult;
      }>(CREATE_BLOCKED_PERIOD_MUTATION, {
        input: {
          fromDate: date,
          toDate: date,
          fromTime: null,
          toTime: null,
          reason: reason.trim().length === 0 ? null : reason.trim(),
        },
        forceFlag,
      });
      return res.createBlockedPeriod;
    },
    onSuccess: () => {
      invalidate();
    },
  });

  const unblockMutation = useMutation({
    mutationFn: async () => {
      const res = await gqlRequest<{
        unblockDay: { success: boolean; unblockedDate: string };
      }>(UNBLOCK_DAY_MUTATION, { date });
      return res.unblockDay;
    },
    onSuccess: () => {
      invalidate();
    },
  });

  const result = blockMutation.data;
  const isWarning = Boolean(
    blockMutation.isSuccess && result && result.requiresForce,
  );

  if (dayState === "OUTSIDE_WINDOW" || dayState === "BLOCKED") {
    return createPortal(
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4"
        data-testid="calendar-day-tap-unblock-dialog"
      >
        <div className="bg-white rounded-lg shadow-lg ring-1 ring-divider w-full max-w-md p-6">
          <h2 className="font-heading text-lg mb-3">
            {t("admin.calendar.dayTap.unblockTitle")}
          </h2>
          <p className="text-sm mb-4">
            <strong>{date}</strong>
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-outline"
              onClick={onClose}
              data-testid="calendar-day-tap-cancel"
            >
              {t("admin.calendar.dayTap.unblockCancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => unblockMutation.mutate()}
              disabled={unblockMutation.isPending}
              data-testid="calendar-day-tap-unblock-confirm"
            >
              {t("admin.calendar.dayTap.unblockConfirm")}
            </button>
          </div>
          {unblockMutation.isError && (
            <p
              role="alert"
              className="text-error mt-3 text-sm"
              data-testid="calendar-day-tap-error"
            >
              {unblockMutation.error instanceof ClientError
                ? (unblockMutation.error.response.errors?.[0]?.message ??
                  t("admin.calendar.dayTap.errorFallback"))
                : t("admin.calendar.dayTap.errorFallback")}
            </p>
          )}
        </div>
      </div>,
      document.body,
    );
  }

  // AVAILABLE / FULL — block dialog. 2-phase dry-run / force flow.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4"
      data-testid="calendar-day-tap-block-dialog"
    >
      <div className="bg-white rounded-lg shadow-lg ring-1 ring-divider w-full max-w-md p-6">
        <h2 className="font-heading text-lg mb-3">
          {t("admin.calendar.dayTap.blockTitle")}
        </h2>
        <p className="text-sm mb-4">
          <strong>{date}</strong>
        </p>

        {!isWarning && (
          <>
            <label className="block text-sm mb-2">
              {t("availability.admin.blockedPeriod.reason")}
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="field w-full mt-1"
                data-testid="calendar-day-tap-reason"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-outline"
                onClick={onClose}
                data-testid="calendar-day-tap-cancel"
              >
                {t("admin.calendar.dayTap.unblockCancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => blockMutation.mutate(false)}
                disabled={blockMutation.isPending}
                data-testid="calendar-day-tap-block-submit"
              >
                {t("availability.admin.blockedPeriod.save")}
              </button>
            </div>
          </>
        )}

        {isWarning && result && (
          <div data-testid="calendar-day-tap-b5b-warning">
            <p className="text-sm font-medium mb-2 text-error">
              {t("availability.admin.blockedPeriod.conflictTitle")}
            </p>
            <ul className="text-sm mb-3">
              {result.conflicts.map((c) => (
                <li
                  key={c.id}
                  className="py-1"
                  data-testid="calendar-day-tap-conflict-row"
                >
                  {c.date} {c.time ?? "—"} · {c.partySize} pers.
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input
                type="checkbox"
                checked={forceAck}
                onChange={(e) => setForceAck(e.target.checked)}
                data-testid="calendar-day-tap-b5b-checkbox"
              />
              {t("availability.admin.blockedPeriod.conflictAck")}
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-outline"
                onClick={onClose}
                data-testid="calendar-day-tap-cancel"
              >
                {t("admin.calendar.dayTap.unblockCancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!forceAck || blockMutation.isPending}
                onClick={() => blockMutation.mutate(true)}
                data-testid="calendar-day-tap-b5b-confirm"
              >
                {t("availability.admin.blockedPeriod.conflictConfirm")}
              </button>
            </div>
          </div>
        )}

        {blockMutation.isError && (
          <p
            role="alert"
            className="text-error mt-3 text-sm"
            data-testid="calendar-day-tap-error"
          >
            {blockMutation.error instanceof ClientError
              ? (blockMutation.error.response.errors?.[0]?.message ??
                t("admin.calendar.dayTap.errorFallback"))
              : t("admin.calendar.dayTap.errorFallback")}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
